import { useEffect, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';

import { appeler } from '../../api/client.ts';
import { useSession } from '../../composants/session.tsx';
import { useDiffere } from '../../composants/use-differe.ts';
import { Entete } from '../../composants/mise-en-page.tsx';
import { Chargement, DateHeure, Erreur, formaterDateHeure } from '../../composants/communs.tsx';
import {
  Badge,
  BoutonImprimer,
  DataTable,
  Montant,
  Nombre,
  Pagination,
  Sheet,
  formaterFcfa,
  exporterTableauPdf,
  type Colonne,
} from '../../composants/ui/index.ts';
import { useApercuPdf } from '../../composants/apercu-pdf.tsx';
import { DetailVenteVue } from './detail.tsx';
import type { ReponseVentes, Vente } from './types.ts';

/**
 * Ventes — RG-062, RG-161.
 *
 * La portée renvoyée par le serveur est affichée telle quelle : un vendeur lit
 * « vos ventes », un administrateur « la boutique ». Laisser croire à un
 * vendeur qu'il voit tout serait pire que de ne rien afficher.
 */
export function Ventes(): ReactNode {
  const { boutiqueId } = useSession();
  const [saisie, setSaisie] = useState('');
  const numero = useDiffere(saisie.trim(), 350);
  const [dateDebut, setDateDebut] = useState('');
  const [dateFin, setDateFin] = useState('');
  const [page, setPage] = useState(1);
  const limite = 25;

  // Un filtre qui change doit retomber en page 1 : rester en page 3 d'une
  // recherche qui n'a plus que deux résultats afficherait un écran vide.
  useEffect(() => {
    setPage(1);
  }, [boutiqueId, numero, dateDebut, dateFin]);

  const { data, isPending, error } = useQuery({
    queryKey: ['ventes', boutiqueId, numero, dateDebut, dateFin, page],
    queryFn: () => {
      const parametres = new URLSearchParams();
      if (boutiqueId !== null) parametres.set('boutique_id', boutiqueId);
      // Deux caractères au moins : le serveur refuse en dessous.
      if (numero.length >= 2) parametres.set('numero', numero);
      if (dateDebut !== '') parametres.set('date_debut', dateDebut);
      if (dateFin !== '') parametres.set('date_fin', dateFin);
      parametres.set('page', String(page));
      parametres.set('limite', String(limite));
      return appeler<ReponseVentes>(`/ventes?${parametres.toString()}`);
    },
  });

  return (
    <>
      <Entete
        titre="Ventes"
        sous={
          data?.portee === 'PERSONNELLE'
            ? 'Vos ventes'
            : boutiqueId === null
              ? 'Historique de toutes vos boutiques'
              : 'Historique de la boutique'
        }
        actions={
          /* Le client revient avec son ticket : on tape la fin du numéro
             imprimé (« 4F2A ») et la vente apparaît. */
          <label>
            <span className="visuellement-cache">Numéro du ticket</span>
            <input
              type="search"
              placeholder="N° du ticket, ex. 4F2A"
              value={saisie}
              onChange={(evenement) => {
                setSaisie(evenement.target.value);
              }}
            />
          </label>
        }
      />

      <div className="ligne" style={{ marginBottom: 12 }}>
        <div className="champ" style={{ margin: 0 }}>
          <label htmlFor="ventes-date-debut">Depuis</label>
          <input
            id="ventes-date-debut"
            type="date"
            value={dateDebut}
            // Une fin antérieure au nouveau début n'aurait plus de sens.
            max={dateFin === '' ? undefined : dateFin}
            onChange={(evenement) => {
              setDateDebut(evenement.target.value);
            }}
          />
        </div>
        <div className="champ" style={{ margin: 0 }}>
          <label htmlFor="ventes-date-fin">Jusqu’au</label>
          <input
            id="ventes-date-fin"
            type="date"
            value={dateFin}
            min={dateDebut === '' ? undefined : dateDebut}
            onChange={(evenement) => {
              setDateFin(evenement.target.value);
            }}
          />
        </div>
        {(dateDebut !== '' || dateFin !== '') && (
          <button
            type="button"
            className="bouton--discret"
            style={{ alignSelf: 'flex-end' }}
            onClick={() => {
              setDateDebut('');
              setDateFin('');
            }}
          >
            Réinitialiser
          </button>
        )}
      </div>

      {isPending && <Chargement />}
      {error !== null && <Erreur erreur={error} />}
      {data !== undefined && (
        <Sheet>
          <Liste ventes={data.donnees} consolide={boutiqueId === null} />
          <Pagination page={data.page} limite={data.limite} total={data.total} onPage={setPage} />
        </Sheet>
      )}
    </>
  );
}

/** Une boutique par groupe, dans l'ordre de leur PREMIÈRE apparition — pas
 * un tri alphabétique, qui mélangerait l'ordre naturel (le plus récent
 * d'abord) entre boutiques. */
function regrouperParBoutique(ventes: Vente[]): [string, Vente[]][] {
  const carte = new Map<string, Vente[]>();
  for (const vente of ventes) {
    const groupe = carte.get(vente.boutique);
    if (groupe === undefined) {
      carte.set(vente.boutique, [vente]);
    } else {
      groupe.push(vente);
    }
  }
  return Array.from(carte.entries());
}

function Liste({
  ventes,
  consolide,
}: {
  ventes: Vente[];
  consolide: boolean;
}): ReactNode {
  const { ouvrir } = useApercuPdf();
  // Sans la colonne Boutique : en vue consolidée, le regroupement ci-dessous
  // la remplace — un sous-titre par boutique dit déjà laquelle, sans la
  // répéter sur chaque ligne.
  const colonnes: Colonne<Vente>[] = [
    {
      cle: 'numero',
      entete: 'N°',
      // Le numéro est le point d'entrée du détail : c'est lui qu'un
      // client cite quand il conteste, et lui qu'on cherche.
      rendu: (vente) => <Link to={`/ventes/${vente.id}`}>{vente.numero}</Link>,
      texte: (vente) => vente.numero,
    },
    {
      cle: 'date',
      entete: 'Date',
      rendu: (vente) => <DateHeure valeur={vente.horodatage_serveur} />,
      texte: (vente) => formaterDateHeure(vente.horodatage_serveur),
    },
    {
      cle: 'vendeur',
      entete: 'Vendeur',
      rendu: (vente) => vente.vendeur,
      texte: (vente) => vente.vendeur,
    },
    {
      cle: 'canal',
      entete: 'Canal',
      rendu: (vente) => vente.canal,
      texte: (vente) => vente.canal,
    },
    {
      cle: 'statut',
      entete: 'Statut',
      rendu: (vente) =>
        vente.statut === 'CONTREPASSEE' ? (
          <Badge statut="contrepassee" />
        ) : (
          <Badge statut="valide" libelle="Validée" />
        ),
      texte: (vente) => (vente.statut === 'CONTREPASSEE' ? 'Contrepassée' : 'Validée'),
    },
    {
      cle: 'lignes',
      entete: 'Lignes',
      numerique: true,
      rendu: (vente) => <Nombre valeur={vente.nb_lignes} />,
      texte: (vente) => String(vente.nb_lignes),
    },
    {
      cle: 'total',
      entete: 'Montant',
      numerique: true,
      rendu: (vente) => <Montant valeur={vente.total} />,
      texte: (vente) => formaterFcfa(vente.total),
    },
  ];

  // Le PDF, lui, reste UNE seule liste à plat : le papier ne feuillette pas
  // aussi facilement que l'écran, et la boutique doit rester lisible ligne
  // par ligne. Colonne ajoutée en tête, texte seul (absente à l'écran).
  const colonnesPdf: Colonne<Vente>[] = consolide
    ? [
        {
          cle: 'boutique',
          entete: 'Boutique',
          rendu: () => null,
          texte: (vente: Vente) => vente.boutique,
        },
        ...colonnes,
      ]
    : colonnes;

  const groupes = consolide ? regrouperParBoutique(ventes) : null;

  return (
    <>
      {ventes.length > 0 && (
        <div className="ligne" style={{ justifyContent: 'flex-end', marginBottom: 8 }}>
          <BoutonImprimer
            onClick={() => {
              ouvrir(() =>
                exporterTableauPdf({
                  titre: 'Ventes',
                  colonnes: colonnesPdf,
                  lignes: ventes,
                  nomFichier: 'ventes',
                }),
              );
            }}
          />
        </div>
      )}

      {groupes === null ? (
        <DataTable
          lignes={ventes}
          cleDe={(vente) => vente.id}
          vide="Aucune vente enregistrée."
          colonnes={colonnes}
        />
      ) : ventes.length === 0 ? (
        <DataTable lignes={[]} cleDe={(vente) => vente.id} vide="Aucune vente enregistrée." colonnes={colonnes} />
      ) : (
        <div className="pile">
          {groupes.map(([boutique, lignesBoutique]) => (
            <div key={boutique}>
              <h3 style={{ marginBottom: 8 }}>
                {boutique}{' '}
                <span className="entete__sous" style={{ display: 'inline' }}>
                  ({lignesBoutique.length})
                </span>
              </h3>
              <DataTable
                lignes={lignesBoutique}
                cleDe={(vente) => vente.id}
                vide="Aucune vente enregistrée."
                colonnes={colonnes}
              />
            </div>
          ))}
        </div>
      )}
    </>
  );
}

/** Route de détail : `/ventes/:id`. */
export function VenteDetail(): ReactNode {
  const { id } = useParams<{ id: string }>();

  return (
    <>
      <Entete
        titre="Détail de la vente"
        sous="Lignes, remises, arrondi et règlements"
        actions={<Link to="/ventes">← Retour aux ventes</Link>}
      />
      {id !== undefined && <DetailVenteVue venteId={id} />}
    </>
  );
}
